import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { fetchCurrentOrderForDate, listOrderMembers, OrderRecord, OrderMemberRecord, subscribeOrdersForDate, subscribeOrderMembers, updateOrder, deleteOrder, debugAllListeners, createOrder, getFormattedTimeRemaining, isOrderExpired, fetchOrdersWithFilters, joinOrder, leaveOrder, updateOrderMemberItem } from "../services/lunchBuddy";
import { getTodayDateString } from "../lib/date";
import { useOrderNotifications } from "../hooks/useOrderNotifications";
import ConfirmModal from "../components/ConfirmModal";

interface Props {
  userEmail: string;
  displayName?: string;
}

export default function OrdersPage({ userEmail, displayName }: Props) {
  const [currentOrder, setCurrentOrder] = useState<OrderRecord | null>(null);
  const [members, setMembers] = useState<OrderMemberRecord[]>([]);
  const [loading, setLoading] = useState(true);

  // Filter state
  const [filteredOrders, setFilteredOrders] = useState<OrderRecord[]>([]);
  const [filters, setFilters] = useState({
    date: getTodayDateString(),
    status: 'all' as 'open' | 'closed' | 'processing' | 'all'
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Order expansion and members state
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());
  const [orderMembers, setOrderMembers] = useState<Record<string, OrderMemberRecord[]>>({});
  const [loadingMembers, setLoadingMembers] = useState<Set<string>>(new Set());
  const [joiningOrder, setJoiningOrder] = useState<string | null>(null);
  const [leavingOrder, setLeavingOrder] = useState<string | null>(null);
  
  // Edit modal state
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({
    restaurant: '',
    deadline: 60,
    link: '',
    notes: ''
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Create modal state
  const [isCreating, setIsCreating] = useState(false);
  const [createFormData, setCreateFormData] = useState({
    restaurant: '',
    deadline: 60,
    notes: '',
    link: ''
  });
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  // Track locally created order to avoid double-adding when realtime arrives
  const ignoreNextCreateForId = useRef<string | null>(null);
  // Manage member subscriptions across the list
  const memberSubsRef = useRef<Record<string, () => void>>({});
  const dishInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  
  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    isLoading: boolean;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    isLoading: false
  });

  // Join modal state for entering dish name
  const [joinModal, setJoinModal] = useState<{
    isOpen: boolean;
    orderId: string | null;
    value: string;
  }>({
    isOpen: false,
    orderId: null,
    value: ''
  });
  const joinInputRef = useRef<HTMLInputElement>(null);

  // Enable notifications while user is on this page
  useOrderNotifications({ userEmail, enabled: true });

  useEffect(() => {
    const loadData = async () => {
      try {
        const today = getTodayDateString();
        const order = await fetchCurrentOrderForDate(today);
        setCurrentOrder(order);
        
        if (order) {
          const memberList = await listOrderMembers(order.id);
          setMembers(memberList);
        }
        
        // Load initial filtered orders
        try {
          const orders = await fetchOrdersWithFilters({
            date: filters.date,
            status: filters.status,
            limit: 50
          });
          // Deduplicate by id and sort by createdAt desc just in case
          const dedup = Object.values(
            orders.reduce((acc: Record<string, OrderRecord>, o) => {
              acc[o.id] = o;
              return acc;
            }, {})
          ).sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
          setFilteredOrders(dedup);
          
          // Load members for all orders to show correct counts
          const allOrderMembers: Record<string, OrderMemberRecord[]> = {};
          for (const order of orders) {
            try {
              const memberList = await listOrderMembers(order.id);
              allOrderMembers[order.id] = memberList;
            } catch (error) {
              console.error(`Error loading members for order ${order.id}:`, error);
              allOrderMembers[order.id] = [];
            }
          }
          setOrderMembers(allOrderMembers);
          
        } catch (error) {
          console.error('Error loading initial filtered orders:', error);
        }
        
        // Debug realtime status in OrdersPage
        setTimeout(() => {
          console.log('🔍 OrdersPage: Debugging realtime status...');
          debugAllListeners();
        }, 1000);
        
      } catch (error) {
        console.error('Error loading orders:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Periodic fallback refresh to mitigate realtime hiccups
  useEffect(() => {
    const intervalId = setInterval(() => {
      loadFilteredOrders();
    }, 30000);
    return () => clearInterval(intervalId);
  }, [filters]);

  // Handle edit order
  const handleEditClick = () => {
    if (!currentOrder) return;
    setEditFormData({
      restaurant: currentOrder.restaurant,
      deadline: currentOrder.deadline,
      link: currentOrder.link || '',
      notes: currentOrder.notes || ''
    });
    setIsEditing(true);
  };

  const handleUpdateOrder = async () => {
    if (!currentOrder) return;
    
    setIsUpdating(true);
    try {
      await updateOrder({
        id: currentOrder.id,
        restaurant: editFormData.restaurant,
        deadline: editFormData.deadline,
        link: editFormData.link.trim() || undefined,
        notes: editFormData.notes.trim()
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Có lỗi xảy ra khi cập nhật order');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteOrder = async () => {
    if (!currentOrder) return;
    
    setConfirmModal({
      isOpen: true,
      title: 'Xóa Order',
      message: `Bạn có chắc muốn xóa order tại ${currentOrder.restaurant}? Hành động này không thể hoàn tác.`,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isLoading: true }));
        setIsDeleting(true);
        try {
          console.log('🗑️ Starting delete order process for:', currentOrder.id);
          await deleteOrder(currentOrder.id);
          console.log('✅ Delete order API call completed');
          
          // Force refresh if realtime doesn't work
          setTimeout(() => {
            console.log('⚡ Force checking order status after delete...');
            const today = getTodayDateString();
            fetchCurrentOrderForDate(today).then(order => {
              console.log('🔍 Current order after delete:', order);
              if (!order) {
                console.log('✅ Order successfully deleted, updating UI');
                setCurrentOrder(null);
                setMembers([]);
              } else {
                console.warn('⚠️ Order still exists after delete, something went wrong');
              }
            }).catch(console.error);
          }, 1000);
          
          toast.success('Đã xóa order thành công');
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {}, isLoading: false });
        } catch (error) {
          console.error('❌ Error deleting order:', error);
          toast.error('Có lỗi xảy ra khi xóa order: ' + (error as Error).message);
        } finally {
          setIsDeleting(false);
          setConfirmModal(prev => ({ ...prev, isLoading: false }));
        }
      },
      isLoading: false
    });
  };

  const canEditOrder = currentOrder && currentOrder.createdBy === userEmail;

  // Toggle order expansion
  const toggleOrderExpansion = async (orderId: string) => {
    const isExpanded = expandedOrders.has(orderId);
    
    if (isExpanded) {
      setExpandedOrders(prev => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
    } else {
      setExpandedOrders(prev => {
        const next = new Set(prev);
        next.add(orderId);
        return next;
      });
      
      // Load members if not already loaded
      if (!orderMembers[orderId]) {
        setLoadingMembers(prev => {
          const next = new Set(prev);
          next.add(orderId);
          return next;
        });
        try {
          const memberList = await listOrderMembers(orderId);
          setOrderMembers(prev => ({
            ...prev,
            [orderId]: memberList
          }));
        } catch (error) {
          console.error('Error loading members for order:', orderId, error);
        } finally {
          setLoadingMembers(prev => {
            const next = new Set(prev);
            next.delete(orderId);
            return next;
          });
        }
      }
    }
  };

  // Join order
  const handleJoinOrder = async (orderId: string, itemName?: string) => {
    setJoiningOrder(orderId);
    try {
      await joinOrder(orderId, userEmail, itemName, displayName);
      if (itemName && itemName.trim()) {
        toast.success("Đã tham gia và lưu món của bạn");
      } else {
        toast.success("Đã tham gia order");
      }
      
      // Update members list
      const memberList = await listOrderMembers(orderId);
      setOrderMembers(prev => ({
        ...prev,
        [orderId]: memberList
      }));
      
      // Update current order members if this is the current order
      if (currentOrder?.id === orderId) {
        setMembers(memberList);
      }
      
    } catch (error) {
      console.error('Error joining order:', error);
      toast.error('Có lỗi xảy ra khi tham gia order');
    } finally {
      setJoiningOrder(null);
    }
  };

  // Leave order
  const handleLeaveOrder = async (orderId: string) => {
    setLeavingOrder(orderId);
    try {
      await leaveOrder(orderId, userEmail);
      toast.success('Đã rời order');
      
      // Update members list
      const memberList = await listOrderMembers(orderId);
      setOrderMembers(prev => ({
        ...prev,
        [orderId]: memberList
      }));
      
      // Update current order members if this is the current order
      if (currentOrder?.id === orderId) {
        setMembers(memberList);
      }
      
    } catch (error) {
      console.error('Error leaving order:', error);
      toast.error('Có lỗi xảy ra khi rời order');
    } finally {
      setLeavingOrder(null);
    }
  };

  // Edit specific order
  const handleEditOrder = (order: OrderRecord) => {
    setEditFormData({
      restaurant: order.restaurant,
      deadline: order.deadline,
      link: order.link || '',
      notes: order.notes || ''
    });
    setCurrentOrder(order); // Set as current order for editing
    setIsEditing(true);
  };

  // Delete specific order
  const handleDeleteSpecificOrder = async (order: OrderRecord) => {
    setConfirmModal({
      isOpen: true,
      title: 'Xóa Order',
      message: `Bạn có chắc muốn xóa order tại ${order.restaurant}? Hành động này không thể hoàn tác.`,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isLoading: true }));
        setIsDeleting(true);
        try {
          await deleteOrder(order.id);
          
          // Remove from filtered orders
          setFilteredOrders(prev => prev.filter(o => o.id !== order.id));
          
          // Remove from order members
          setOrderMembers(prev => {
            const next = { ...prev };
            delete next[order.id];
            return next;
          });
          
          // Clear current order if it matches
          if (currentOrder?.id === order.id) {
            setCurrentOrder(null);
            setMembers([]);
          }
          
          toast.success('Đã xóa order thành công');
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {}, isLoading: false });
        } catch (error) {
          console.error('Error deleting order:', error);
          toast.error('Có lỗi xảy ra khi xóa order');
        } finally {
          setIsDeleting(false);
          setConfirmModal(prev => ({ ...prev, isLoading: false }));
        }
      },
      isLoading: false
    });
  };

  // Load filtered orders
  const loadFilteredOrders = async () => {
    try {
      const orders = await fetchOrdersWithFilters({
        date: filters.date,
        status: filters.status,
        limit: 50
      });
      setFilteredOrders(orders);
      
      // Load members for all filtered orders
      const allOrderMembers: Record<string, OrderMemberRecord[]> = {};
      for (const order of orders) {
        try {
          const memberList = await listOrderMembers(order.id);
          allOrderMembers[order.id] = memberList;
        } catch (error) {
          console.error(`Error loading members for order ${order.id}:`, error);
          allOrderMembers[order.id] = [];
        }
      }
      setOrderMembers(allOrderMembers);
      
      // Set current order if today's date and open status
      if (filters.date === getTodayDateString() && filters.status === 'all') {
        const todayOpenOrder = orders.find(order => order.status === 'open');
        setCurrentOrder(todayOpenOrder || null);
        
        if (todayOpenOrder) {
          const memberList = allOrderMembers[todayOpenOrder.id] || [];
          setMembers(memberList);
        } else {
          setMembers([]);
        }
      }
    } catch (error) {
      console.error('Error loading filtered orders:', error);
    }
  };

  // Load filtered orders when filters change
  useEffect(() => {
    loadFilteredOrders();
  }, [filters]);

  // Handle create order
  const handleCreateClick = () => {
    setCreateFormData({
      restaurant: '',
      deadline: 60,
      notes: '',
      link: ''
    });
    setIsCreating(true);
  };

  const handleCreateOrder = async () => {
    if (!createFormData.restaurant.trim()) return;
    
    setIsCreatingOrder(true);
    try {
      const today = getTodayDateString();
      const created = await createOrder({
        createdBy: userEmail,
        restaurant: createFormData.restaurant.trim(),
        deadline: createFormData.deadline,
        date: today,
        link: createFormData.link.trim() || undefined,
        notes: createFormData.notes.trim() || undefined,
        authorName: displayName
      });
      
      console.log('✅ Order created successfully:', created);
      ignoreNextCreateForId.current = created.id;
      setCurrentOrder(created);
      // Ensure filtered list has only one instance
      setFilteredOrders(prev => {
        const exists = prev.some(o => o.id === created.id);
        return exists ? prev.map(o => (o.id === created.id ? created : o)) : [created, ...prev];
      });
      setMembers([]); // New order starts with no members
      setIsCreating(false);
      
      // Reset form
      setCreateFormData({
        restaurant: '',
        deadline: 60,
        notes: '',
        link: ''
      });
      
    } catch (error) {
      console.error('❌ Error creating order:', error);
      toast.error('Có lỗi xảy ra khi tạo order: ' + (error as Error).message);
    } finally {
      setIsCreatingOrder(false);
    }
  };

  // Subscribe to realtime order changes
  useEffect(() => {
    const today = getTodayDateString();
    
    const unsubscribeOrders = subscribeOrdersForDate(today, (payload) => {
      console.log('📦 Order change detected:', payload);
      
      if (payload.action === 'create') {
        // Ignore if event is for a different date than filter
        if (payload.record.date !== today) return;
        if (ignoreNextCreateForId.current === payload.record.id) {
          // We already set it locally; update existing row instead of inserting duplicate
          ignoreNextCreateForId.current = null;
          setCurrentOrder(payload.record);
          setFilteredOrders(prev => prev.map(o => o.id === payload.record.id ? payload.record : o));
        } else {
          // Update current order and prepend if not exist
          setCurrentOrder(payload.record);
          setFilteredOrders(prev => {
            const exists = prev.some(o => o.id === payload.record.id);
            return exists ? prev.map(o => o.id === payload.record.id ? payload.record : o) : [payload.record, ...prev];
          });
        }
        // Load members for the new order
        listOrderMembers(payload.record.id)
          .then(memberList => {
            setMembers(memberList);
            setOrderMembers(prev => ({
              ...prev,
              [payload.record.id]: memberList
            }));
          })
          .catch(console.error);
      } else if (payload.action === 'update') {
        if (payload.record.date !== today) return;
        // Update current order if it matches
        setCurrentOrder((prevOrder: OrderRecord | null) => {
          if (prevOrder && prevOrder.id === payload.record.id) {
            console.log('✅ Updating current order with realtime data');
            return payload.record;
          }
          return prevOrder;
        });
        // Update filtered orders list
        setFilteredOrders(prev => prev.map(order => 
          order.id === payload.record.id ? payload.record : order
        ));
      } else if (payload.action === 'delete') {
        // For delete, we can't rely on date; always remove by id
        // Remove current order if it matches
        setCurrentOrder((prevOrder: OrderRecord | null) => {
          if (prevOrder && prevOrder.id === payload.record.id) {
            console.log('✅ Removing current order due to realtime delete');
            setMembers([]);
            return null;
          }
          return prevOrder;
        });
        // Remove from filtered orders list (dedupe as well)
        setFilteredOrders(prev => prev.filter(order => order.id !== payload.record.id));
        // Remove from order members
        setOrderMembers(prev => {
          const next = { ...prev };
          delete next[payload.record.id];
          return next;
        });
      }
    });

    return () => unsubscribeOrders();
  }, []); // Remove dependency on currentOrder?.id

  // Subscribe to order member changes for all filtered orders (keeps counts in sync)
  useEffect(() => {
    const currentIds = new Set(filteredOrders.map(o => String(o.id)));
    const subs = memberSubsRef.current;

    // Unsubscribe removed IDs
    Object.keys(subs).forEach((orderId) => {
      if (!currentIds.has(orderId)) {
        try { subs[orderId]?.(); } catch {}
        delete subs[orderId];
      }
    });

    // Subscribe new IDs
    filteredOrders.forEach(order => {
      const id = String(order.id);
      if (subs[id]) return;
      subs[id] = subscribeOrderMembers(order.id, ({ action, record }) => {
        setOrderMembers(prev => {
          const existing = prev[record.orderId] || [];
          if (action === 'create') {
            // Avoid duplicates
            const exists = existing.some(m => m.id === record.id);
            if (exists) return prev;
            return { ...prev, [record.orderId]: [...existing, record] };
          } else if (action === 'delete') {
            if (!record.orderId) {
              // Safe refetch if orderId missing on delete event
              listOrderMembers(order.id).then(memberList => {
                setOrderMembers(p => ({ ...p, [order.id]: memberList }));
                if (currentOrder?.id === order.id) setMembers(memberList);
              });
              return prev;
            }
            return { ...prev, [record.orderId]: existing.filter(m => m.id !== record.id) };
          }
          return prev;
        });

        // If current order matches, mirror to top-level 'members'
        setCurrentOrder(prev => {
          if (prev && prev.id === record.orderId) {
            setMembers(members => {
              if (action === 'create') {
                const exists = members.some(m => m.id === record.id);
                return exists ? members : [...members, record];
              }
              if (action === 'delete') {
                return members.filter(m => m.id !== record.id);
              }
              return members;
            });
          }
          return prev;
        });
      });
    });

    return () => {
      // On unmount, cleanup all
      Object.values(memberSubsRef.current).forEach(unsub => {
        try { unsub(); } catch {}
      });
      memberSubsRef.current = {};
    };
  }, [filteredOrders]);

  // Subscribe to realtime member changes for current order
  useEffect(() => {
    if (!currentOrder) return;

    const unsubscribeMembers = subscribeOrderMembers(currentOrder.id, (payload) => {
      console.log('👥 Order member change detected:', payload);
      
      if (payload.action === 'create') {
        setMembers(prev => [...prev, payload.record]);
        // Also update orderMembers for consistent count display
        setOrderMembers(prev => ({
          ...prev,
          [currentOrder.id]: [...(prev[currentOrder.id] || []), payload.record]
        }));
      } else if (payload.action === 'delete') {
        setMembers(prev => prev.filter(member => member.id !== payload.record.id));
        // Also update orderMembers for consistent count display
        setOrderMembers(prev => ({
          ...prev,
          [currentOrder.id]: (prev[currentOrder.id] || []).filter(member => member.id !== payload.record.id)
        }));
      }
    });

    return () => unsubscribeMembers();
  }, [currentOrder?.id]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 flex items-center justify-center min-h-[50vh]">
        <div className="text-white text-xl">⏳ Đang tải...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
        <header className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-orange-500/20 to-red-500/20 border border-orange-500/30 mb-4">
            <span className="text-3xl">📦</span>
          </div>
          <h1 className="text-4xl font-bold text-white mb-2">
            Quản lý Orders
          </h1>
          <p className="text-slate-300 text-xl">
            Xem chi tiết các đơn đặt đồ ăn
          </p>
        </header>

        {/* Filters Section */}
        <div className="max-w-4xl mx-auto mb-6">
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-600/50 rounded-2xl p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <span>🔍</span>
                  Bộ lọc
                </h3>
                
                <div className="flex items-center gap-3">
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Ngày</label>
                    <input
                      type="date"
                      value={filters.date}
                      onChange={(e) => setFilters(prev => ({ ...prev, date: e.target.value }))}
                      className="px-3 py-1.5 rounded-lg bg-slate-700 border border-slate-600 text-white text-sm focus:outline-none focus:border-orange-500 transition-colors"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Trạng thái</label>
                    <select
                      value={filters.status}
                      onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value as any }))}
                      className="px-3 py-1.5 rounded-lg bg-slate-700 border border-slate-600 text-white text-sm focus:outline-none focus:border-orange-500 transition-colors"
                    >
                      <option value="all">Tất cả</option>
                      <option value="open">Đang mở</option>
                      <option value="closed">Đã đóng</option>
                      <option value="processing">Đang xử lý</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="text-sm text-slate-400">
                {filteredOrders.length} orders
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto">
          {currentOrder && filteredOrders.length === 0 ? (
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-600/50 rounded-2xl p-6 mb-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500/20 to-blue-500/20 border border-emerald-500/30 flex items-center justify-center">
                    <span className="text-xl">🏪</span>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">{currentOrder.restaurant}</h2>
                    <p className={`text-sm font-medium ${isOrderExpired(currentOrder) ? 'text-red-400' : 'text-orange-400'}`}>
                      {isOrderExpired(currentOrder) ? '⏰ Đã hết hạn' : `⏰ ${getFormattedTimeRemaining(currentOrder)}`}
                    </p>
                    <p className="text-slate-500 text-sm">Tạo bởi: {currentOrder.author || currentOrder.createdBy}</p>
                    {currentOrder.link && (
                      <p className="text-slate-500 text-sm mt-1">
                        <span className="mr-1">🔗</span>
                        <a 
                          href={currentOrder.link.startsWith('http') ? currentOrder.link : `https://${currentOrder.link}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-400 hover:text-blue-300 underline"
                        >
                          Link quán ăn
                        </a>
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Edit/Delete buttons - only show for order creator */}
                {canEditOrder && (
                  <div className="flex gap-2">
                    <button
                      onClick={handleEditClick}
                      disabled={isUpdating || isDeleting}
                      className="flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-500/20 border border-blue-500/30 text-blue-300 hover:bg-blue-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span>✏️</span>
                      <span className="hidden sm:inline">Sửa</span>
                    </button>
                    <button
                      onClick={handleDeleteOrder}
                      disabled={isUpdating || isDeleting}
                      className="flex items-center gap-2 px-3 py-2 rounded-xl bg-red-500/20 border border-red-500/30 text-red-300 hover:bg-red-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isDeleting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-red-300 border-t-transparent rounded-full animate-spin"></div>
                          <span className="hidden sm:inline">Đang xóa...</span>
                        </>
                      ) : (
                        <>
                          <span>🗑️</span>
                          <span className="hidden sm:inline">Xóa</span>
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>

              <div className="border-t border-slate-600/30 pt-6">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <span>👥</span>
                  Danh sách tham gia ({members.length})
                </h3>
                
                {members.length > 0 ? (
                  <div className="grid gap-3">
                    {members.map((member) => (
                      <div
                        key={member.id}
                        className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                          member.userEmail === userEmail
                            ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
                            : 'bg-slate-700/50 border-slate-600/30 text-slate-300'
                        }`}
                      >
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500/30 to-purple-500/30 border border-blue-500/50 flex items-center justify-center">
                          <span className="text-sm">👤</span>
                        </div>
                        <div className="font-medium">
                          <div>
                            {member.userName || member.userEmail}
                            {member.itemName && <span className="text-xs text-slate-400 ml-2">- {member.itemName}</span>}
                            {member.userEmail === userEmail && <span className="text-xs ml-2">(Bạn)</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-400">
                    <span className="text-4xl block mb-2">🤷‍♂️</span>
                    Chưa có ai tham gia
                  </div>
                )}
              </div>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-600/50 rounded-2xl p-8 text-center">
              <span className="text-6xl block mb-4">📭</span>
              <h2 className="text-2xl font-bold text-white mb-2">Không có order nào</h2>
              <p className="text-slate-400 mb-6">
                {filters.date === getTodayDateString() 
                  ? "Hôm nay chưa có ai tạo đơn đặt đồ ăn"
                  : `Không có order nào cho ngày ${filters.date}`
                }
              </p>
              
              {/* Create Order Button */}
              <button
                onClick={handleCreateClick}
                className="inline-flex items-center gap-3 px-6 py-3 rounded-xl bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold text-lg transition-all duration-300 hover:scale-105 active:scale-95 shadow-lg shadow-orange-500/25"
              >
                <span className="text-2xl">🚀</span>
                Tạo Order Mới
              </button>
              
              {/* Quick Info */}
              <div className="max-w-md mx-auto mt-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-slate-400">
                  <div className="flex items-center gap-2">
                    <span>🏪</span>
                    <span>Chọn cửa hàng yêu thích</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span>⏰</span>
                    <span>Đặt thời gian deadline</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span>👥</span>
                    <span>Mời team tham gia</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span>📱</span>
                    <span>Quản lý realtime</span>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {/* Filtered Orders List */}
        {filteredOrders.length > 0 && (
          <div className="max-w-4xl mx-auto mt-8">
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-600/50 rounded-2xl p-6">
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <span>📋</span>
                Danh sách Orders ({filteredOrders.length})
              </h3>
              
              <div className="grid gap-4">
                {filteredOrders.map((order) => {
                  const isExpanded = expandedOrders.has(order.id);
                  const members = orderMembers[order.id] || [];
                  const isLoadingMembers = loadingMembers.has(order.id);
                  const isUserMember = members.some(m => m.userEmail === userEmail);
                  const canEdit = order.createdBy === userEmail;
                  const isJoining = joiningOrder === order.id;
                  const isLeaving = leavingOrder === order.id;

                  return (
                    <div
                      key={order.id}
                      className={`rounded-xl transition-all duration-300 border ${
                        currentOrder?.id === order.id 
                          ? 'bg-gradient-to-r from-emerald-500/10 to-blue-500/10 border-emerald-500/30 shadow-lg' 
                          : 'bg-slate-700/50 border-slate-600/30 hover:bg-slate-700/70'
                      } ${isExpanded ? 'shadow-xl' : ''}`}
                    >
                      {/* Order Header - Always visible */}
                      <div 
                        className="p-4 cursor-pointer select-none"
                        onClick={() => toggleOrderExpansion(order.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500/20 to-blue-500/20 border border-emerald-500/30 flex items-center justify-center">
                              <span className="text-lg">🏪</span>
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h4 className="font-semibold text-white">{order.restaurant}</h4>
                                {currentOrder?.id === order.id && (
                                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                                    🎯 Hiện tại
                                  </span>
                                )}
                                {isUserMember && (
                                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-300 border border-blue-500/30">
                                    👥 Đã tham gia
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-4 text-sm text-slate-400 mt-2">
                                <span>📅 {order.date}</span>
                                <span className={`
                                  px-2 py-1 rounded-full text-xs font-medium
                                  ${order.status === 'open' ? 'bg-green-500/20 text-green-300' : 
                                    order.status === 'closed' ? 'bg-red-500/20 text-red-300' : 
                                    'bg-yellow-500/20 text-yellow-300'}
                                `}>
                                  {order.status === 'open' ? '🟢 Đang mở' : 
                                   order.status === 'closed' ? '🔴 Đã đóng' : 
                                   '🟡 Đang xử lý'}
                                </span>
                                <span>👥 {(orderMembers[order.id] || []).length} người</span>
                                {order.link && (
                                  <a 
                                    href={order.link.startsWith('http') ? order.link : `https://${order.link}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1 px-2 py-1 rounded-lg bg-blue-500/20 border border-blue-500/30 text-blue-300 hover:bg-blue-500/30 transition-all text-xs font-medium"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <span>🔗</span>
                                    <span className="hidden sm:inline">Menu</span>
                                  </a>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3">
                            <div className="text-right text-sm">
                              <div className={`font-medium ${isOrderExpired(order) ? 'text-red-400' : 'text-orange-400'}`}>
                                {isOrderExpired(order) ? '⏰ Đã hết hạn' : getFormattedTimeRemaining(order)}
                              </div>
                              <div className="text-slate-500">
                                bởi {order.createdBy === userEmail ? 'Bạn' : (order.author || order.createdBy.split('@')[0])}
                              </div>
                            </div>
                            <div className={`transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
                              <span className="text-slate-400">🔽</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Expanded Content */}
                      {isExpanded && (
                        <div className="px-4 pb-4 border-t border-slate-600/30">
                          <div className="pt-4 space-y-4">
                            
                            {/* Order Details */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {order.link && (
                                <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-900/50">
                                  <span className="text-lg">🔗</span>
                                  <div>
                                    <div className="text-xs text-slate-400 mb-1">Link quán ăn</div>
                                    <a 
                                      href={order.link.startsWith('http') ? order.link : `https://${order.link}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-400 hover:text-blue-300 underline text-sm"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      Xem menu & đặt hàng
                                    </a>
                                  </div>
                                </div>
                              )}
                              
                              {/* <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-900/50">
                                <span className="text-lg">📊</span>
                                <div>
                                  <div className="text-xs text-slate-400 mb-1">Trạng thái</div>
                                  <div className="text-sm font-medium text-white">
                                    {order.status === 'open' ? '🟢 Đang nhận order' : 
                                     order.status === 'closed' ? '🔴 Đã chốt order' : 
                                     '🟡 Đang xử lý'}
                                  </div>
                                </div>
                              </div> */}

                              {/* Notes */}
                              <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-900/50">
                                <span className="text-lg">📝</span>
                                <div className="flex-1">
                                  <div className="text-xs text-slate-400 mb-1">Ghi chú</div>
                                  {canEdit ? (
                                    <textarea
                                      defaultValue={order.notes || ''}
                                      rows={2}
                                      placeholder="VD: Ghi chú cho team, lưu ý quán, phí ship..."
                                      className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:border-emerald-500"
                                      onBlur={async (e) => {
                                        const value = e.currentTarget.value.trim();
                                        try {
                                          await updateOrder({ id: order.id, notes: value });
                                          setFilteredOrders(prev => prev.map(o => o.id === order.id ? { ...o, notes: value } : o));
                                          setCurrentOrder(prev => (prev && prev.id === order.id) ? { ...prev, notes: value } : prev);
                                        } catch (err) {
                                          console.error('Error updating notes:', err);
                                          toast.error('Không cập nhật được ghi chú');
                                        }
                                      }}
                                    />
                                  ) : (
                                    <div className="text-sm text-slate-300 whitespace-pre-wrap">
                                      {order.notes || 'Không có ghi chú'}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* My dish input */}
                              <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-900/50">
                                <span className="text-lg">🍽️</span>
                                <div className="flex-1">
                                  <div className="text-xs text-slate-400 mb-1">Món của bạn</div>
                                  <div className="flex gap-2">
                                    <input
                                      ref={(el) => { dishInputRefs.current[order.id] = el; }}
                                      type="text"
                                      defaultValue={(members.find(m => m.userEmail === userEmail)?.itemName) || ''}
                                      placeholder="Ví dụ: Bún bò, Cơm gà, Trà sữa..."
                                      className="w-full px-3 py-2.5 rounded-xl bg-slate-800 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:border-emerald-500 placeholder-slate-500 shadow-inner"
                                      onKeyDown={async (e) => {
                                        if (e.key === 'Enter') {
                                          const raw = (e.currentTarget as HTMLInputElement).value;
                                          const value = raw.trim() || null;
                                          try {
                                            if (isUserMember) {
                                              await updateOrderMemberItem(order.id, userEmail, value);
                                            } else {
                                              await joinOrder(order.id, userEmail, value || undefined, displayName);
                                            }
                                            const memberList = await listOrderMembers(order.id);
                                            setOrderMembers(prev => ({ ...prev, [order.id]: memberList }));
                                            if (currentOrder?.id === order.id) setMembers(memberList);
                                            toast.success(value ? 'Đã lưu món của bạn' : (isUserMember ? 'Đã xóa món' : 'Đã tham gia order'));
                                          } catch (err) {
                                            console.error('Error updating item name:', err);
                                            toast.error('Không cập nhật được tên món');
                                          }
                                        }
                                      }}
                                    />
                                    <button
                                      className="px-3 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium transition-colors"
                                      onClick={async () => {
                                        const raw = dishInputRefs.current[order.id]?.value ?? '';
                                        const value = raw.trim() || null;
                                        try {
                                          if (isUserMember) {
                                            await updateOrderMemberItem(order.id, userEmail, value);
                                          } else {
                                            await joinOrder(order.id, userEmail, value || undefined, displayName);
                                          }
                                          const memberList = await listOrderMembers(order.id);
                                          setOrderMembers(prev => ({ ...prev, [order.id]: memberList }));
                                          if (currentOrder?.id === order.id) setMembers(memberList);
                                          toast.success(value ? 'Đã lưu món của bạn' : (isUserMember ? 'Đã xóa món' : 'Đã tham gia order'));
                                        } catch (err) {
                                          console.error('Error updating item name:', err);
                                          toast.error('Không cập nhật được tên món');
                                        }
                                      }}
                                    >
                                      Lưu
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Members List */}
                            <div>
                              <h5 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
                                <span>👥</span>
                                Danh sách tham gia ({members.length})
                              </h5>
                              
                              <div className="rounded-lg bg-slate-900/50 p-3">
                                {isLoadingMembers ? (
                                  <div className="flex items-center justify-center py-4 text-slate-400">
                                    <div className="animate-spin w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full mr-2"></div>
                                    Đang tải...
                                  </div>
                                ) : members.length > 0 ? (
                                  <div className="grid gap-2">
                                    {members.map((member) => (
                                      <div
                                        key={member.id}
                                        className={`flex items-center gap-2 p-2 rounded-lg transition-colors ${
                                          member.userEmail === userEmail
                                            ? 'bg-emerald-500/10 border border-emerald-500/30'
                                            : 'bg-slate-800/50'
                                        }`}
                                      >
                                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500/30 to-purple-500/30 border border-blue-500/50 flex items-center justify-center">
                                          <span className="text-xs">👤</span>
                                        </div>
                                        <span className="text-sm text-slate-200 font-medium">
                                          {member.userName || member.userEmail}
                                          {member.itemName && <span className="text-xs text-slate-400 ml-2">- {member.itemName}</span>}
                                          {member.userEmail === userEmail && (
                                            <span className="text-xs ml-2 text-emerald-300">(Bạn)</span>
                                          )}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="text-center py-4 text-slate-400">
                                    <span className="text-2xl block mb-2">🤷‍♂️</span>
                                    <span className="text-sm">Chưa có ai tham gia</span>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex flex-wrap gap-2">
                              {/* Join/Leave Button */}
                              {order.status === 'open' && !isOrderExpired(order) ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (isUserMember) {
                                      handleLeaveOrder(order.id);
                                    } else {
                                      setJoinModal({ isOpen: true, orderId: order.id, value: '' });
                                    }
                                  }}
                                  disabled={isJoining || isLeaving}
                                  className={`flex items-center gap-2 px-3 py-2 rounded-lg font-medium text-sm transition-all ${
                                    isUserMember
                                      ? 'bg-red-500/20 border border-red-500/30 text-red-300 hover:bg-red-500/30'
                                      : 'bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/30'
                                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                                >
                                  {(isJoining || isLeaving) ? (
                                    <>
                                      <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin"></div>
                                      <span>{isJoining ? 'Đang tham gia...' : 'Đang rời...'}</span>
                                    </>
                                  ) : (
                                    <>
                                      <span>{isUserMember ? '👋' : '🎉'}</span>
                                      <span>{isUserMember ? 'Rời order' : 'Tham gia'}</span>
                                    </>
                                  )}
                                </button>
                              ) : order.status === 'open' && isOrderExpired(order) ? (
                                // Expired order - show disabled message
                                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm font-medium">
                                  <span>⏰</span>
                                  <span>Đã hết hạn</span>
                                </div>
                              ) : null}

                              {/* Share Button */}
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  const shareUrl = `${window.location.origin}/order/${order.id}`;
                                  
                                  if (navigator.share) {
                                    try {
                                      await navigator.share({
                                        title: `Order tại ${order.restaurant}`,
                                        text: `Tham gia order tại ${order.restaurant}`,
                                        url: shareUrl
                                      });
                                    } catch (error) {
                                      console.error('Error sharing:', error);
                                    }
                                  } else {
                                    try {
                                      await navigator.clipboard.writeText(shareUrl);
                                      toast.success('Đã copy link order vào clipboard');
                                    } catch (error) {
                                      console.error('Error copying to clipboard:', error);
                                      toast.error('Không thể copy link');
                                    }
                                  }
                                }}
                                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/30 transition-all font-medium text-sm"
                              >
                                <span>📤</span>
                                <span>Chia sẻ</span>
                              </button>

                              {/* View Detail Button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(`/order/${order.id}`, '_blank');
                                }}
                                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-purple-500/20 border border-purple-500/30 text-purple-300 hover:bg-purple-500/30 transition-all font-medium text-sm"
                              >
                                <span>👁️</span>
                                <span>Chi tiết</span>
                              </button>

                              {/* Edit/Delete Buttons for Creator */}
                              {canEdit && (
                                <>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditOrder(order);
                                    }}
                                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-500/20 border border-blue-500/30 text-blue-300 hover:bg-blue-500/30 transition-all font-medium text-sm"
                                  >
                                    <span>✏️</span>
                                    <span>Sửa</span>
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteSpecificOrder(order);
                                    }}
                                    disabled={isDeleting}
                                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/20 border border-red-500/30 text-red-300 hover:bg-red-500/30 transition-all font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {isDeleting ? (
                                      <>
                                        <div className="w-3 h-3 border border-red-300 border-t-transparent rounded-full animate-spin"></div>
                                        <span>Đang xóa...</span>
                                      </>
                                    ) : (
                                      <>
                                        <span>🗑️</span>
                                        <span>Xóa</span>
                                      </>
                                    )}
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Floating Create Order Button - always available */}
        {!isCreating && !isEditing && (
          <button
            onClick={handleCreateClick}
            title="Tạo order mới"
            className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white rounded-full shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 transition-all duration-300 hover:scale-110 active:scale-95 flex items-center justify-center z-40"
          >
            <span className="text-2xl">➕</span>
          </button>
        )}

        {/* Edit Order Modal */}
        {isEditing && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-slate-800 border border-slate-600 rounded-2xl p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <span>✏️</span>
                  Sửa Order
                </h3>
                <button
                  onClick={() => setIsEditing(false)}
                  disabled={isUpdating}
                  className="text-slate-400 hover:text-white transition-colors disabled:cursor-not-allowed"
                >
                  <span className="text-xl">❌</span>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Tên cửa hàng
                  </label>
                  <input
                    type="text"
                    value={editFormData.restaurant}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, restaurant: e.target.value }))}
                    className="w-full px-3 py-2 rounded-xl bg-slate-700 border border-slate-600 text-white placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors"
                    placeholder="Tên cửa hàng..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Thời gian order (phút)
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <select
                      value={editFormData.deadline}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, deadline: parseInt(e.target.value) }))}
                      className="w-full px-3 py-2 rounded-xl bg-slate-700 border border-slate-600 text-white focus:outline-none focus:border-blue-500 transition-colors"
                    >
                      <option value={15}>15 phút</option>
                      <option value={30}>30 phút</option>
                      <option value={45}>45 phút</option>
                      <option value={60}>1 giờ</option>
                      <option value={90}>1.5 giờ</option>
                      <option value={120}>2 giờ</option>
                      <option value={180}>3 giờ</option>
                    </select>
                    <input
                      type="number"
                      min="5"
                      max="480"
                      step="5"
                      value={editFormData.deadline}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, deadline: parseInt(e.target.value) || 60 }))}
                      className="w-full px-3 py-2 rounded-xl bg-slate-700 border border-slate-600 text-white focus:outline-none focus:border-blue-500 transition-colors"
                      placeholder="Phút"
                    />
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    Thời gian mọi người có để chọn món
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Link quán ăn (tùy chọn)
                  </label>
                  <input
                    type="url"
                    value={editFormData.link}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, link: e.target.value }))}
                    className="w-full px-3 py-2 rounded-xl bg-slate-700 border border-slate-600 text-white placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors"
                    placeholder="https://shopee.com/shop123 hoặc link menu..."
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Link menu, fanpage để team dễ tham khảo
                  </p>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setIsEditing(false)}
                  disabled={isUpdating}
                  className="flex-1 px-4 py-2 rounded-xl bg-slate-600 text-slate-300 hover:bg-slate-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Hủy
                </button>
                <button
                  onClick={handleUpdateOrder}
                  disabled={isUpdating || !editFormData.restaurant.trim()}
                  className="flex-1 px-4 py-2 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUpdating ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Đang cập nhật...
                    </div>
                  ) : (
                    'Cập nhật'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create Order Modal */}
        {isCreating && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-slate-800 border border-slate-600 rounded-2xl p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <span>🚀</span>
                  Tạo Order Mới
                </h3>
                <button
                  onClick={() => setIsCreating(false)}
                  disabled={isCreatingOrder}
                  className="text-slate-400 hover:text-white transition-colors disabled:cursor-not-allowed"
                >
                  <span className="text-xl">❌</span>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Tên cửa hàng
                  </label>
                  <input
                    type="text"
                    value={createFormData.restaurant}
                    onChange={(e) => setCreateFormData(prev => ({ ...prev, restaurant: e.target.value }))}
                    className="w-full px-3 py-2 rounded-xl bg-slate-700 border border-slate-600 text-white placeholder-slate-400 focus:outline-none focus:border-orange-500 transition-colors"
                    placeholder="Ví dụ: KFC, Lotteria, Cơm tấm Sài Gòn..."
                    autoFocus
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Thời gian order (phút)
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <select
                      value={createFormData.deadline}
                      onChange={(e) => setCreateFormData(prev => ({ ...prev, deadline: parseInt(e.target.value) }))}
                      className="w-full px-3 py-2 rounded-xl bg-slate-700 border border-slate-600 text-white focus:outline-none focus:border-orange-500 transition-colors"
                    >
                      <option value={15}>15 phút</option>
                      <option value={30}>30 phút</option>
                      <option value={45}>45 phút</option>
                      <option value={60}>1 giờ</option>
                      <option value={90}>1.5 giờ</option>
                      <option value={120}>2 giờ</option>
                      <option value={180}>3 giờ</option>
                    </select>
                    <input
                      type="number"
                      min="5"
                      max="480"
                      step="5"
                      value={createFormData.deadline}
                      onChange={(e) => setCreateFormData(prev => ({ ...prev, deadline: parseInt(e.target.value) || 60 }))}
                      className="w-full px-3 py-2 rounded-xl bg-slate-700 border border-slate-600 text-white focus:outline-none focus:border-orange-500 transition-colors"
                      placeholder="Phút"
                    />
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    Thời gian mọi người có để chọn món (tính từ lúc tạo)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Link quán ăn (tùy chọn)
                  </label>
                  <input
                    type="url"
                    value={createFormData.link}
                    onChange={(e) => setCreateFormData(prev => ({ ...prev, link: e.target.value }))}
                    className="w-full px-3 py-2 rounded-xl bg-slate-700 border border-slate-600 text-white placeholder-slate-400 focus:outline-none focus:border-orange-500 transition-colors"
                    placeholder="https://shopee.com/shop123 hoặc link menu..."
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Link menu, fanpage để team dễ tham khảo
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Ghi chú (tùy chọn)
                  </label>
                  <textarea
                    value={createFormData.notes}
                    onChange={(e) => setCreateFormData(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full px-3 py-2 rounded-xl bg-slate-700 border border-slate-600 text-white placeholder-slate-400 focus:outline-none focus:border-orange-500 transition-colors resize-none"
                    placeholder="Thông tin về menu, giá cả, thanh toán..."
                    rows={3}
                  />
                </div>

                {/* Preview */}
                {createFormData.restaurant.trim() && (
                  <div className="p-3 rounded-xl bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-500/20">
                    <h4 className="text-sm font-medium text-orange-300 mb-2 flex items-center gap-1">
                      <span>👀</span> Xem trước
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span>🏪</span>
                        <span className="text-white font-medium">{createFormData.restaurant}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span>⏰</span>
                        <span className="text-slate-300">
                          {createFormData.deadline >= 60 
                            ? `${Math.floor(createFormData.deadline / 60)}h ${createFormData.deadline % 60}m`
                            : `${createFormData.deadline}m`} để chọn món
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span>👤</span>
                        <span className="text-slate-300">Tạo bởi: {displayName || userEmail}</span>
                      </div>
                      {createFormData.link.trim() && (
                        <div className="flex gap-2">
                          <span>🔗</span>
                          <a 
                            href={createFormData.link.startsWith('http') ? createFormData.link : `https://${createFormData.link}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-400 hover:text-blue-300 underline text-xs break-all"
                          >
                            {createFormData.link}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setIsCreating(false)}
                  disabled={isCreatingOrder}
                  className="flex-1 px-4 py-2 rounded-xl bg-slate-600 text-slate-300 hover:bg-slate-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Hủy
                </button>
                <button
                  onClick={handleCreateOrder}
                  disabled={isCreatingOrder || !createFormData.restaurant.trim()}
                  className="flex-1 px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCreatingOrder ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Đang tạo...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <span>🚀</span>
                      Tạo Order
                    </div>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Join Modal */}
        {joinModal.isOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-slate-800 border border-slate-600 rounded-2xl p-6 w-full max-w-md">
              <div className="mb-4 flex items-center justify-between">
                <h4 className="text-white font-bold flex items-center gap-2">
                  <span>🍽️</span>
                  Nhập tên món (tùy chọn)
                </h4>
                <button
                  className="text-slate-400 hover:text-white"
                  onClick={() => setJoinModal({ isOpen: false, orderId: null, value: "" })}
                >
                  ✖
                </button>
              </div>
              <input
                autoFocus
                ref={joinInputRef}
                type="text"
                placeholder="Ví dụ: Bún bò, Cơm tấm, Trà sữa..."
                onKeyDown={async (e) => {
                  if (e.key === 'Enter') {
                    const orderId = joinModal.orderId!;
                    const raw = joinInputRef.current?.value || '';
                    const val = raw.trim() || undefined;
                    try {
                      await handleJoinOrder(orderId, val);
                      setJoinModal({ isOpen: false, orderId: null, value: "" });
                    } catch (err) {
                      console.error(err);
                      toast.error('Không tham gia được order');
                    }
                  }
                }}
                className="w-full px-4 py-3 rounded-xl bg-slate-900 border border-slate-700 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500"
              />
              <div className="mt-4 flex gap-3 justify-end">
                <button
                  className="px-4 py-2 rounded-xl bg-slate-700 text-slate-300 hover:bg-slate-600"
                  onClick={() => setJoinModal({ isOpen: false, orderId: null, value: "" })}
                >
                  Hủy
                </button>
                <button
                  className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={async () => {
                    const orderId = joinModal.orderId!;
                    const raw = joinInputRef.current?.value || '';
                    const val = raw.trim() || undefined;
                    try {
                      await handleJoinOrder(orderId, val);
                      setJoinModal({ isOpen: false, orderId: null, value: "" });
                    } catch (err) {
                      console.error(err);
                      toast.error('Không tham gia được order');
                    }
                  }}
                >
                  Tham gia
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Confirm Modal */}
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          title={confirmModal.title}
          message={confirmModal.message}
          confirmText="Xóa"
          cancelText="Hủy"
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {}, isLoading: false })}
          isLoading={confirmModal.isLoading}
          type="danger"
        />
      </div>
  );
}
